/**
 * Offline Context Provider
 * Manages online/offline status and provides offline queue functionality
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { initDB } from '@/lib/offlineDB';
import { syncOfflineActions, getPendingCount, getFailedCount } from '@/lib/offlineQueue';
import { processPhotoUploads, getPhotoUploadCount } from '@/lib/photoUploadManager';
import { cacheOrgData, cacheOrgSettings, cacheUserSettings, getLastSyncTime, getCacheStats } from '@/lib/offlineSync';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  
  const wasOfflineRef = useRef(!navigator.onLine);
  const syncTimeoutRef = useRef(null);

  // Initialize IndexedDB
  useEffect(() => {
    const init = async () => {
      try {
        await initDB();
        setIsInitialized(true);
        console.log('[OfflineContext] IndexedDB initialized');
        
        // Get initial pending count
        const count = await getPendingCount();
        setPendingCount(count);
        const fc = await getFailedCount();
        setFailedCount(fc);
      } catch (error) {
        console.error('[OfflineContext] Failed to initialize IndexedDB:', error);
      }
    };
    init();
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineContext] Online');
      setIsOnline(true);
      
      // If we were offline, show "back online" toast and sync
      if (wasOfflineRef.current) {
        toast.success('Back online!', {
          description: pendingCount > 0 
            ? `Syncing ${pendingCount} pending change${pendingCount > 1 ? 's' : ''}...`
            : 'Your connection has been restored.',
          id: 'back-online'
        });
        
        // Trigger sync after a short delay
        if (pendingCount > 0) {
          syncTimeoutRef.current = setTimeout(() => {
            syncPendingActions();
          }, 1000);
        }
      }
      wasOfflineRef.current = false;
    };

    const handleOffline = () => {
      console.log('[OfflineContext] Offline');
      setIsOnline(false);
      wasOfflineRef.current = true;
      
      // Show offline toast
      toast.warning('You\'re offline', {
        description: 'Don\'t worry - your changes will be saved locally.',
        id: 'went-offline',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for service worker sync messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SYNC_REQUESTED') {
          syncPendingActions();
        }
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [pendingCount]);

  // Sync pending actions when back online.
  // `force=true` (passed by user-driven "Sync now" / "Retry" buttons) skips
  // exponential backoff so the click actually attempts the network. Without
  // this, a button tap is a silent no-op when items are in a 15-min backoff
  // window — the exact SolidHome stuck-queue symptom (Feb 2026).
  const syncPendingActions = useCallback(async ({ force = false } = {}) => {
    if (isSyncing || !isOnline) return null;
    
    setIsSyncing(true);
    
    try {
      // 1. Drain photo uploads first (small, fast, frees up text saves)
      try {
        await processPhotoUploads({ force });
      } catch (e) {
        console.warn('[OfflineContext] Photo processor error:', e);
      }
      
      // 2. Then text-only queue items
      const result = await syncOfflineActions((progress) => {
        console.log('[OfflineContext] Sync progress:', progress);
      }, { force });
      
      if (result.total > 0) {
        if (result.authError) {
          toast.error('Sign in to finish syncing', {
            description: 'Your session expired. Sign in again, then tap Sync.',
            id: 'sync-auth-error',
            duration: 10000,
          });
        } else if (result.timedOut) {
          toast.warning('Sync paused', {
            description: `${result.success} synced. ${result.deferred + result.failed} still queued — will retry automatically.`,
            id: 'sync-timeout',
          });
        } else if (result.failed === 0 && result.deferred === 0) {
          toast.success('Sync complete', {
            description: `${result.success} change${result.success > 1 ? 's' : ''} synced.`,
            id: 'sync-complete',
          });
        } else if (result.failed > 0) {
          toast.warning(`${result.success} synced · ${result.failed} need attention`, {
            description: 'Some changes could not sync. They\'ll retry automatically.',
            id: 'sync-partial',
          });
        } else if (result.deferred > 0) {
          toast.message(`${result.success} synced · ${result.deferred} retrying`, {
            description: 'Some items will retry automatically with backoff.',
            id: 'sync-deferred',
          });
        }
      }
      
      // Update counts
      const newCount = await getPendingCount();
      setPendingCount(newCount);
      const newFailed = await getFailedCount();
      setFailedCount(newFailed);
      
      return result;
    } catch (error) {
      console.error('[OfflineContext] Sync failed:', error);
      toast.error('Sync failed', {
        description: 'Some changes couldn\'t be synced. We\'ll try again later.',
        id: 'sync-error',
      });
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline]);

  // iPad Safari hygiene: flush queue when tab returns to foreground / page becomes visible
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        // Tiny delay to let any pending IndexedDB writes settle
        setTimeout(() => {
          getPendingCount().then((n) => {
            if (n > 0) syncPendingActions();
          });
        }, 500);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [syncPendingActions]);

  // Background retry tick — every 30s, kick the queue if there are pending items
  // (catches the case where backoff-deferred items become due)
  useEffect(() => {
    if (!isInitialized) return;
    const t = setInterval(async () => {
      if (!navigator.onLine || isSyncing) return;
      const [n, photos] = await Promise.all([getPendingCount(), getPhotoUploadCount()]);
      if (n > 0 || photos.pending > 0) syncPendingActions();
    }, 30_000);
    return () => clearInterval(t);
  }, [isInitialized, isSyncing, syncPendingActions]);

  // Cache organization data for offline use
  const cacheData = useCallback(async (orgId, org = null, user = null) => {
    if (!isInitialized) {
      console.warn('[OfflineContext] DB not initialized, skipping cache');
      return;
    }
    
    try {
      console.log('[OfflineContext] Starting data cache for org:', orgId);
      
      // Cache org and user settings first (quick)
      if (org) {
        await cacheOrgSettings({ ...org, id: orgId });
      }
      if (user) {
        await cacheUserSettings(user);
      }
      
      // Cache main data (clients, estimates, jobs, invoices)
      await cacheOrgData(orgId, {
        onProgress: (progress) => {
          console.log('[OfflineContext] Cache progress:', progress);
        }
      });
      
      // Update last sync time and stats
      const syncTime = await getLastSyncTime(orgId);
      setLastSyncTime(syncTime);
      
      const stats = await getCacheStats(orgId);
      setCacheStats(stats);
      
      console.log('[OfflineContext] Data cached successfully:', stats);
      
      return stats;
    } catch (error) {
      console.error('[OfflineContext] Failed to cache data:', error);
      // Don't show error toast - caching is background operation
      return null;
    }
  }, [isInitialized]);

  // Update pending count manually
  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
    const fcount = await getFailedCount();
    setFailedCount(fcount);
    return count;
  }, []);

  // Get cache stats for an org
  const getStats = useCallback(async (orgId) => {
    if (!isInitialized) return null;
    
    try {
      const stats = await getCacheStats(orgId);
      setCacheStats(stats);
      return stats;
    } catch (error) {
      console.error('[OfflineContext] Failed to get cache stats:', error);
      return null;
    }
  }, [isInitialized]);

  const value = {
    isOnline,
    isInitialized,
    pendingCount,
    failedCount,
    isSyncing,
    lastSyncTime,
    cacheStats,
    syncPendingActions,
    cacheData,
    refreshPendingCount,
    getStats
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
